import { Pencil } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SectionCard } from "@/components/ui/section-card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ServiceUserApiKeysPanel } from "@/features/service-users/components/ServiceUserApiKeysPanel";
import { ServiceUserFormDialog } from "@/features/service-users/components/ServiceUserFormDialog";
import { ServiceUserTeamsPanel } from "@/features/service-users/components/ServiceUserTeamsPanel";
import { ServiceUserUsageDocs } from "@/features/service-users/components/ServiceUserUsageDocs";
import { ServiceUserUsagePanel } from "@/features/service-users/components/ServiceUserUsagePanel";
import { ServiceUserWebhookPanel } from "@/features/service-users/components/ServiceUserWebhookPanel";
import { useServiceUser } from "@/features/service-users/hooks/useServiceUsers";
import { Action } from "@/permissions/actions";
import { useCan } from "@/permissions/hooks";

function formatDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

export function ServiceUserDetailPage() {
  const { t } = useTranslation("serviceUsers");
  const { serviceUserId = "" } = useParams();
  const { data, isLoading, isError, error, refetch } =
    useServiceUser(serviceUserId);
  const canManage = useCan(Action.SERVICE_USER_UPDATED);
  const [editOpen, setEditOpen] = useState(false);

  if (isLoading) return <LoadingState />;
  if (isError || !data)
    return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={data.name}
        description={`${t("detail.status")}: ${
          data.is_active ? t("detail.active") : t("detail.inactive")
        }`}
        actions={
          <Button
            variant="outline"
            onClick={() => setEditOpen(true)}
            disabled={!canManage}
          >
            <Pencil className="h-4 w-4" />
            {t("form.titleEdit")}
          </Button>
        }
      />

      <Tabs defaultValue="data">
        <TabsList className="flex-wrap">
          <TabsTrigger value="data">{t("tabs.data")}</TabsTrigger>
          <TabsTrigger value="keys">{t("tabs.keys")}</TabsTrigger>
          <TabsTrigger value="teams">{t("tabs.teams")}</TabsTrigger>
          <TabsTrigger value="webhook">{t("tabs.webhook")}</TabsTrigger>
          <TabsTrigger value="usage">{t("tabs.usage")}</TabsTrigger>
          <TabsTrigger value="docs">{t("tabs.docs")}</TabsTrigger>
        </TabsList>

        <TabsContent value="data">
          <SectionCard
            title={t("detail.dataTitle")}
            description={t("detail.dataDescription")}
          >
            <dl className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <dt className="text-xs font-medium text-ink-500">
                  {t("form.name")}
                </dt>
                <dd className="text-sm text-ink-950">{data.name}</dd>
              </div>
              <div className="flex flex-col gap-1">
                <dt className="text-xs font-medium text-ink-500">
                  {t("detail.status")}
                </dt>
                <dd className="text-sm text-ink-950">
                  {data.is_active ? (
                    <Badge variant="secondary">{t("detail.active")}</Badge>
                  ) : (
                    <Badge variant="destructive">{t("detail.inactive")}</Badge>
                  )}
                </dd>
              </div>
              <div className="flex flex-col gap-1">
                <dt className="text-xs font-medium text-ink-500">
                  {t("detail.createdAt")}
                </dt>
                <dd className="text-sm text-ink-950">
                  {formatDate(data.created_at)}
                </dd>
              </div>
            </dl>
          </SectionCard>
        </TabsContent>

        <TabsContent value="keys">
          <ServiceUserApiKeysPanel serviceUserId={serviceUserId} />
        </TabsContent>

        <TabsContent value="teams">
          <ServiceUserTeamsPanel serviceUserId={serviceUserId} />
        </TabsContent>

        <TabsContent value="webhook">
          <ServiceUserWebhookPanel serviceUserId={serviceUserId} />
        </TabsContent>

        <TabsContent value="usage">
          <ServiceUserUsagePanel serviceUserId={serviceUserId} />
        </TabsContent>

        <TabsContent value="docs">
          <ServiceUserUsageDocs />
        </TabsContent>
      </Tabs>

      <ServiceUserFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        serviceUser={data}
      />
    </div>
  );
}
